import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Share2, Link2, Check, MessageCircle, Mail } from 'lucide-react';

/**
 * Iter 159 — Share buttons with defensive URL validation.
 *
 * Two supported call shapes:
 *   1. <ShareButtons businessId={id} businessName={name} description={desc} />
 *      → builds /business/{id}
 *   2. <ShareButtons url={absoluteOrRelativeUrl} title={name} description={desc} />
 *      → uses the supplied URL verbatim (after sanity check)
 *
 * Defensive guards (Fix 2):
 *   - businessId is treated as invalid when it's missing, the literal strings
 *     "undefined" / "null" / "false", or whitespace-only.
 *   - When the resolved URL is invalid the entire bar is hidden so users can
 *     never share /business/undefined etc.
 *
 * Host (Fix 1):
 *   - Defaults to window.location.origin so Preview and Production each share
 *     their own correct host. Falls back to www.find-2-day.com on the
 *     server-rendered path.
 */
const INVALID_IDS = new Set(['', 'undefined', 'null', 'false', 'nan']);

const isValidId = (v) => {
  if (v === undefined || v === null) return false;
  const s = String(v).trim().toLowerCase();
  return s.length > 0 && !INVALID_IDS.has(s);
};

const resolveHost = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return 'https://www.find-2-day.com';
};

const ShareButtons = ({ businessId, businessName, description, url, title }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  // Resolve a valid share URL, or null when we don't have enough to share.
  let shareUrl = null;
  if (url && typeof url === 'string' && url.trim()) {
    // Caller provided an explicit URL (used by ClassifiedDetail / LostFoundDetail).
    // Treat it as the source of truth — but skip the obviously-broken cases.
    const trimmed = url.trim();
    if (!trimmed.endsWith('/undefined') && !trimmed.endsWith('/null')) {
      shareUrl = trimmed;
    }
  } else if (isValidId(businessId)) {
    shareUrl = `${resolveHost()}/api/share/business/${encodeURIComponent(String(businessId).trim())}`;
  }

  // Bail entirely when we cannot produce a valid URL — never render a share
  // bar that would link to /business/undefined.
  if (!shareUrl) return null;

  const displayName = businessName || title || 'Find-2-Day';
  const shareText = `${displayName} - ${description || t('shareDefaultText', 'Check out this on Find-2-Day')}`;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: displayName, text: shareText, url: shareUrl });
        return;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
      }
    }
    // Fallback: open email share
    const subject = encodeURIComponent(displayName);
    const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`, '_blank', 'noopener');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(displayName);
    const body = encodeURIComponent(`${shareText}\n\n${shareUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <div className="px-4 py-3 border-b bg-gray-50" data-testid="share-bar">
      <p className="text-xs text-gray-500 mb-2" data-testid="share-label">
        {t('shareThisBusiness', 'Share this business with others')}
      </p>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={handleShare}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 border border-purple-200 rounded-lg text-sm font-semibold hover:bg-purple-100 transition-colors cursor-pointer"
          data-testid="share-native-btn"
        >
          <Share2 className="h-4 w-4" />
          {t('share', 'Share')}
        </button>

        <button
          onClick={handleWhatsApp}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-xs font-medium hover:bg-green-100 transition-colors"
          data-testid="share-whatsapp-btn"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          WhatsApp
        </button>

        <button
          onClick={copyToClipboard}
          className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-xs font-medium transition-colors ${
            copied
              ? 'bg-purple-50 text-purple-700 border-purple-200'
              : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'
          }`}
          data-testid="share-copy-btn"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
          {copied ? t('linkCopied', 'Copied!') : t('copyLink', 'Copy Link')}
        </button>

        <button
          onClick={handleEmail}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 border border-blue-200 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
          data-testid="share-email-btn"
        >
          <Mail className="h-3.5 w-3.5" />
          Email
        </button>
      </div>
    </div>
  );
};

export default ShareButtons;
